import React, { useEffect, useState } from 'react';
import { Download, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { isPreviewableImage } from './workItemFileItems';

/**
 * Saved attachment: image preview, readable rename (metadata only), download/remove.
 */
const WorkItemAttachmentView = ({
  workItemId,
  attachment,
  downloadWorkItemAttachment,
  removeWorkItemAttachment,
  renameWorkItemAttachment,
  showAlert,
  onItemUpdated,
  client = false,
  compact = false,
}) => {
  const opts = client ? { client: true } : {};
  const [previewUrl, setPreviewUrl] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(attachment.name || '');
  const canPreview = isPreviewableImage(attachment.contentType, attachment.name || attachment.originalName);

  useEffect(() => {
    setDraftName(attachment.name || '');
  }, [attachment.name]);

  useEffect(() => {
    let cancelled = false;
    if (!canPreview || !downloadWorkItemAttachment) return undefined;
    (async () => {
      const result = await downloadWorkItemAttachment(workItemId, attachment.id, opts);
      if (!cancelled && result?.ok) setPreviewUrl(result.url);
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment.id, canPreview, workItemId]);

  const saveName = async () => {
    const next = (draftName || '').trim();
    setEditing(false);
    if (!next || next === attachment.name || !renameWorkItemAttachment) return;
    const result = await renameWorkItemAttachment(workItemId, attachment.id, next, opts);
    if (result?.ok) onItemUpdated(result.item);
    else showAlert(true, result?.message || 'Rename failed.', 'error', 5000);
  };

  const thumb = previewUrl ? (
    <button
      type='button'
      className='shrink-0 overflow-hidden rounded border border-border bg-muted'
      onClick={() => setLightbox(true)}
      aria-label={`Preview ${attachment.name}`}>
      <img
        src={previewUrl}
        alt=''
        className={compact ? 'h-10 w-10 object-cover' : 'h-14 w-14 object-cover'}
      />
    </button>
  ) : null;

  return (
    <div className={`flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
      {thumb}
      <div className='min-w-0 flex-1'>
        {editing ? (
          <Input
            autoFocus
            value={draftName}
            maxLength={200}
            className='h-8 text-xs'
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                setDraftName(attachment.name || '');
                setEditing(false);
              }
            }}
          />
        ) : (
          <span className='truncate block text-foreground'>
            {attachment.name}
            {attachment.visibility === 'internal' ? (
              <span className='ml-1 text-xs text-muted-foreground'>internal</span>
            ) : null}
          </span>
        )}
      </div>
      <span className='flex shrink-0 gap-0.5'>
        {renameWorkItemAttachment ? (
          <Button
            size='icon'
            variant='ghost'
            className='h-6 w-6'
            onClick={() => setEditing(true)}
            aria-label='Rename file'>
            <Pencil size={12} strokeWidth={1.5} />
          </Button>
        ) : null}
        <Button
          size='icon'
          variant='ghost'
          className='h-6 w-6'
          onClick={async () => {
            const result = await downloadWorkItemAttachment(workItemId, attachment.id, opts);
            if (result?.ok && result.url) window.open(result.url, '_blank', 'noopener');
            else showAlert(true, result?.message || 'Download failed.', 'error', 5000);
          }}
          aria-label='Download file'>
          <Download size={12} strokeWidth={1.5} />
        </Button>
        {removeWorkItemAttachment ? (
          <Button
            size='icon'
            variant='ghost'
            className='h-6 w-6'
            onClick={async () => {
              const result = await removeWorkItemAttachment(workItemId, attachment.id, opts);
              if (result?.ok) onItemUpdated(result.item);
              else showAlert(true, result?.message || 'Remove failed.', 'error', 5000);
            }}
            aria-label='Remove file'>
            <Trash2 size={12} strokeWidth={1.5} />
          </Button>
        ) : null}
      </span>
      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent className='max-w-3xl bg-card p-0' onClose={() => setLightbox(false)}>
          <DialogHeader className='px-4 py-3'>
            <DialogTitle className='text-sm truncate'>{attachment.name}</DialogTitle>
          </DialogHeader>
          {previewUrl ? (
            <img src={previewUrl} alt={attachment.name} className='max-h-[80vh] w-full object-contain bg-muted' />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkItemAttachmentView;
